import axios, { AxiosError } from 'axios'

import { checkAndUpdateAuthToken, discardAdminSession } from '@/composables/useAuthentication'
import { BASE_URL } from '@/constants/api'

export const axiosPlugin = axios.create({
  baseURL: BASE_URL,
})

axiosPlugin.interceptors.request.use(
  async (req) => {
    if (localStorage.getItem('gc')) {
      req.headers['x-gc'] = localStorage.getItem('gc')
    }
    if (req.headers['Authorization']) {
      const token = await checkAndUpdateAuthToken()
      if (token) {
        req.headers['Authorization'] = `Bearer ${token}`
      }
    }

    // Custom params serializer to handle arrays and objects properly
    req.paramsSerializer = (params: unknown) => {
      if (params && Object.keys(params).length > 0) {
        const paramsObj = params as Record<string, unknown>
        return Object.keys(params)
          .map((key) => {
            const value = paramsObj[key]
            if (typeof value === 'object' && value !== null) {
              return `${key}=${JSON.stringify(value)}`
            }
            return `${key}=${encodeURIComponent(String(value ?? ''))}`
          })
          .join('&')
      }
      return params?.toString() || ''
    }

    return req
  },
  (error) => {
    console.error(`[api-call]: ${error.message} (${error.code})`)
    return Promise.reject(error)
  },
)

// Shared response-error handler so any axios instance (the main `axiosPlugin`, or the bare
// `authClient` in admin-auth.ts) reacts to a revoked/expired session the same way.
export function redirectOnUnauthorized(error: AxiosError) {
  console.error('[API Response Error]:', error.message)

  // Redirect on unauthorized errors only for authenticated requests (an
  // expired/invalid session). Login flows (e.g. SSO) carry no Authorization
  // header — their 401 means bad credentials and must surface inline instead.
  if (error.response?.status === 401 && error.config?.headers?.['Authorization']) {
    // Clear the session directly rather than leaving it to SignIn.vue's onMounted
    // to notice stale `isAuthenticated` state — that indirection meant a dead
    // access token could still be reused (and re-fail) by whatever ran next.
    discardAdminSession()
    window.location.href = '/login?error=unauthorized'
  }

  return Promise.reject(error)
}

axiosPlugin.interceptors.response.use((response) => response, redirectOnUnauthorized)

export default axiosPlugin
